import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import './style.css'
import '../../styles/page-titles.css'
import styled, { keyframes } from 'styled-components';
import 'bootstrap/dist/css/bootstrap.min.css';
import { SidebarContext } from "../../AppRoutes";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

import moment from 'moment'

import { AuthContext } from "../../contexts/auth";

import { getPlansAsaas, deleteAsaasPlan } from "../../services/api";
import { formatPriceWithSymbol } from '../../utils/currency';

import PermissionGate from "../../services/PermissionGate";

import { useNavigate } from "react-router-dom";

import ConfirmDialog from "../../components/ConfirmDialog";

import * as FiIcons from 'react-icons/fi'
import * as AiIcons from 'react-icons/ai'
import * as SlIcons from 'react-icons/sl'

// Keyframe animations
const slideUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const rowSlide = keyframes`
    from {
        opacity: 0;
        transform: translateX(-10px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const scaleIn = keyframes`
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;

// Main page wrapper
const PageWrapper = styled.div`
    display: flex;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background: #ffffff;
    overflow: initial;
    z-index: 9;
    position: relative;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

// Page header
const PageHeader = styled.div`
    animation: ${slideUp} 0.5s ease-out;
    margin-bottom: 24px;

    h1 {
        font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
        font-size: 28px;
        font-weight: 700;
        color: #1e293b;
        margin: 0;
        letter-spacing: -0.5px;
        position: relative;
        display: inline-block;

        &::after {
            content: '';
            position: absolute;
            bottom: -8px;
            left: 0;
            width: 40px;
            height: 3px;
            background: linear-gradient(90deg, #318cd5, #4fa3e8);
            border-radius: 2px;
        }
    }
`;

// Button group container
const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    animation: ${fadeIn} 0.5s ease-out 0.2s backwards;
`;

// Primary button (Add)
const PrimaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 24px;
    font-family: 'Segoe UI', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #318cd5, #2574b8);
    color: white;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(180deg, rgba(255,255,255,0.15) 0%, transparent 50%);
        pointer-events: none;
    }

    &:hover {
        background: linear-gradient(135deg, #4fa3e8, #318cd5);
        transform: translateY(-2px);
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1), 0 0 20px rgba(49, 140, 213, 0.3);
    }

    &:active {
        transform: translateY(0);
    }

    svg {
        transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    &:hover svg {
        transform: scale(1.1);
    }
`;

// Secondary button (Refresh)
const SecondaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 24px;
    font-family: 'Segoe UI', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    cursor: pointer;
    transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
    background: #ffffff;
    color: #318cd5;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    &:hover {
        border-color: #318cd5;
        background: rgba(49, 140, 213, 0.05);
        transform: translateY(-2px);
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }

    &:active {
        transform: translateY(0);
    }

    svg {
        transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    &:hover svg {
        animation: ${spin} 0.6s ease-in-out;
    }
`;

// Table container
const TableContainer = styled.div`
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    animation: ${scaleIn} 0.5s ease-out 0.1s backwards;
    border: 1px solid #e2e8f0;
`;

// Styled table
const StyledTable = styled.table`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;

    thead {
        background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
    }

    thead tr {
        border-bottom: 2px solid #318cd5;
    }

    th {
        padding: 16px 20px;
        text-align: left;
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        color: #64748b;
        position: relative;
    }

    th::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: linear-gradient(90deg, #318cd5, #4fa3e8);
    }

    tbody tr {
        transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
        animation: ${rowSlide} 0.4s ease-out backwards;
        border-bottom: 1px solid #e2e8f0;
    }

    tbody tr:nth-child(1) { animation-delay: 0.05s; }
    tbody tr:nth-child(2) { animation-delay: 0.1s; }
    tbody tr:nth-child(3) { animation-delay: 0.15s; }
    tbody tr:nth-child(4) { animation-delay: 0.2s; }
    tbody tr:nth-child(5) { animation-delay: 0.25s; }
    tbody tr:nth-child(6) { animation-delay: 0.3s; }
    tbody tr:nth-child(7) { animation-delay: 0.35s; }
    tbody tr:nth-child(8) { animation-delay: 0.4s; }
    tbody tr:nth-child(9) { animation-delay: 0.45s; }
    tbody tr:nth-child(10) { animation-delay: 0.5s; }

    tbody tr:hover {
        background: linear-gradient(90deg, rgba(49, 140, 213, 0.08), transparent);
        transform: scale(1.005);
        box-shadow: inset 4px 0 0 #318cd5;
    }

    tbody tr:last-child {
        border-bottom: none;
    }

    td {
        padding: 16px 20px;
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-size: 14px;
        color: #1e293b;
        vertical-align: middle;
    }

    td:first-child {
        font-weight: 700;
        color: #318cd5;
    }
`;

// Value cell with special styling
const ValueCell = styled.td`
    font-family: 'SF Mono', 'Consolas', monospace !important;
    font-weight: 600 !important;
    color: #22c55e !important;
`;

// Cycle badge
const CycleBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    background: rgba(49, 140, 213, 0.1);
    color: #2574b8;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

// Action button
const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
    margin-right: 8px;

    &:last-child {
        margin-right: 0;
    }

    &.edit {
        background: linear-gradient(135deg, #318cd5, #2574b8);
        color: white;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

        &:hover {
            transform: translateY(-2px) scale(1.1);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 0 15px rgba(49, 140, 213, 0.4);
        }
    }

    &.delete {
        background: linear-gradient(135deg, #ef4444, #dc2626);
        color: white;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

        &:hover {
            transform: translateY(-2px) scale(1.1);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 0 15px rgba(239, 68, 68, 0.4);
        }
    }
`;

// Loading state
const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    animation: ${fadeIn} 0.3s ease-out;

    .spinner {
        width: 60px;
        height: 60px;
        border: 3px solid #e2e8f0;
        border-top-color: #318cd5;
        border-radius: 50%;
        animation: ${spin} 0.8s linear infinite;
    }

    p {
        margin-top: 16px;
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-size: 14px;
        color: #64748b;
    }
`;

// Empty state
const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    animation: ${fadeIn} 0.5s ease-out;

    svg {
        width: 80px;
        height: 80px;
        color: #94a3b8;
        margin-bottom: 16px;
    }

    h3 {
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-size: 18px;
        font-weight: 600;
        color: #64748b;
        margin: 0 0 8px 0;
    }

    p {
        font-size: 14px;
        color: #94a3b8;
        margin: 0;
    }
`;

const ListPlanosAdmin = ({ list = [] }) => {
    const { t } = useTranslation('plansAdmin');
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();

    const [planos, setPlanos] = useState([]);
    const { sidebar } = useContext(SidebarContext);
    const [loading, setLoading] = useState(true);
    const [refresh, setRefresh] = useState(false);

    // Helper para traduzir ciclos
    const getCycleLabel = (cycle) => {
        switch (cycle) {
            case 'WEEKLY': return t('list.cycles.weekly');
            case 'MONTHLY': return t('list.cycles.monthly');
            case 'YEARLY': return t('list.cycles.yearly');
            default: return cycle;
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const response = await getPlansAsaas();
                const planosData = response.data?.planos?.planos || [];
                setPlanos(planosData);
                process.env.REACT_APP_ISDEVELOP === 'true' && console.log(response.data);
            } catch (error) {
                process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao carregar planos:", error);
                toast(t('list.toasts.loadError'), { autoClose: 5000, type: "error" });
                setPlanos([]);
            } finally {
                setLoading(false);
                setRefresh(false);
            }
        })();
    }, [refresh, t]);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [shouldDelete, setShouldDelete] = useState(false);
    const [idToDelete, setIdToDelete] = useState('');

    const handleDelete = (idRecebido) => {
        setConfirmOpen(true);
        setIdToDelete(idRecebido);
        if (idRecebido) {
            setShouldDelete(true);
        }
    }

    const deleteReg = async () => {
        if (shouldDelete) {
            const userID = user._id;
            deleteAsaasPlan(userID, idToDelete).then(cast => {
                process.env.REACT_APP_ISDEVELOP === 'true' && console.log("RESULT DELETE:", cast);
                if (cast.status === 200) {
                    toast(t('list.toasts.deleteSuccess'), { autoClose: 5000, type: "success" });
                    setRefresh(true);
                } else {
                    toast(t('list.toasts.deleteError'), { autoClose: 5000, type: "error" });
                }
            }).catch(err => {
                process.env.REACT_APP_ISDEVELOP === 'true' && console.log("ERROR:", err);
                toast(t('list.toasts.deleteError'), { autoClose: 5000, type: "error" });
            });
        }
    }

    const handleRefresh = () => {
        setLoading(true);
        setRefresh(true);
    }

    const handleCadastro = () => {
        navigate("/planos-admin");
    }

    const handleEdit = (planoId) => {
        navigate(`/edit-plan-admin/${planoId}`);
    }

    if (loading) {
        return (
            <PermissionGate permissions={['AdminOnly']}>
                <PageWrapper sidebar={sidebar}>
                    <div className="w-100 p-4 p-md-5">
                        <PageHeader>
                            <h1>{t('list.title')}</h1>
                        </PageHeader>

                        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "24px" }}>
                            <ButtonGroup>
                                <PrimaryButton disabled>
                                    <AiIcons.AiOutlinePlusCircle style={{ fontSize: "20px" }} />
                                    {t('list.buttons.add')}
                                </PrimaryButton>

                                <SecondaryButton disabled>
                                    <SlIcons.SlRefresh style={{ fontSize: "16px" }} />
                                    {t('list.buttons.refresh')}
                                </SecondaryButton>
                            </ButtonGroup>
                        </div>

                        <TableContainer>
                            <LoadingContainer>
                                <div className="spinner" />
                                <p>{t('edit.loading')}</p>
                            </LoadingContainer>
                        </TableContainer>
                    </div>
                </PageWrapper>
            </PermissionGate>
        );
    }

    return (
        <>
            <PermissionGate permissions={['AdminOnly']}>
                <ConfirmDialog
                    title={t('list.confirmDelete.title')}
                    open={confirmOpen}
                    setOpen={setConfirmOpen}
                    onConfirm={deleteReg}
                >
                    {t('list.confirmDelete.message')}
                </ConfirmDialog>

                <PageWrapper sidebar={sidebar}>
                    <div className="w-100 p-4 p-md-5">
                        <PageHeader>
                            <h1>{t('list.title')}</h1>
                        </PageHeader>

                        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "24px" }}>
                            <ButtonGroup>
                                <PrimaryButton onClick={handleCadastro}>
                                    <AiIcons.AiOutlinePlusCircle style={{ fontSize: "20px" }} />
                                    {t('list.buttons.add')}
                                </PrimaryButton>

                                <SecondaryButton onClick={handleRefresh}>
                                    <SlIcons.SlRefresh style={{ fontSize: "16px" }} />
                                    {t('list.buttons.refresh')}
                                </SecondaryButton>
                            </ButtonGroup>
                        </div>

                        <TableContainer>
                            {planos && planos.length > 0 ? (
                                <StyledTable>
                                    <thead>
                                        <tr>
                                            <th>{t('list.table.order')}</th>
                                            <th>{t('list.table.planName')}</th>
                                            <th>{t('list.table.identifier')}</th>
                                            <th>{t('list.table.value')}</th>
                                            <th>{t('list.table.cycle')}</th>
                                            <th>{t('list.table.maxCharges')}</th>
                                            <th>{t('list.table.createdAt')}</th>
                                            <th>{t('list.table.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {planos
                                            .sort((a, b) => a.order - b.order)
                                            .map((plano) => (
                                                <tr key={plano._id}>
                                                    <td>{plano.order}</td>
                                                    <td>{plano.name}</td>
                                                    <td style={{ fontFamily: "'SF Mono', 'Consolas', monospace", color: '#64748b' }}>
                                                        {plano.plan_identifier}
                                                    </td>
                                                    <ValueCell>
                                                        {formatPriceWithSymbol(plano.valor_plano)}
                                                    </ValueCell>
                                                    <td>
                                                        <CycleBadge>{getCycleLabel(plano.plan_cycle)}</CycleBadge>
                                                    </td>
                                                    <td>{plano.max_cycles || '-'}</td>
                                                    <td>
                                                        {plano.createdAt
                                                            ? moment(plano.createdAt).format('DD/MM/YYYY')
                                                            : '-'
                                                        }
                                                    </td>
                                                    <td>
                                                        <ActionButton
                                                            className="edit"
                                                            onClick={() => handleEdit(plano._id)}
                                                            title={t('edit.title')}
                                                        >
                                                            <FiIcons.FiEdit2 style={{ color: "white", fontSize: "16px" }} />
                                                        </ActionButton>
                                                        <ActionButton
                                                            className="delete"
                                                            onClick={() => handleDelete(plano._id)}
                                                            title={t('list.confirmDelete.title')}
                                                        >
                                                            <FiIcons.FiTrash2 style={{ color: "white", fontSize: "16px" }} />
                                                        </ActionButton>
                                                    </td>
                                                </tr>
                                            ))
                                        }
                                    </tbody>
                                </StyledTable>
                            ) : (
                                <EmptyState>
                                    <AiIcons.AiOutlineInbox />
                                    <h3>{t('noPlansRegistered')}</h3>
                                    <p>{t('clickAddFirstPlan')}</p>
                                </EmptyState>
                            )}
                        </TableContainer>
                    </div>
                </PageWrapper>
            </PermissionGate>
        </>
    );
};

export default ListPlanosAdmin;
